import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  EmployeeDetailResponse,
  EmployeeWriteInput,
  EmployeeWriteResponse,
} from './contractsMirror.js';

/**
 * TanStack Query hook for the COARSE single-employee detail endpoint
 * (`GET /api/v1/employees/:id`, 22-05). One query key per employee id. The tenant is
 * derived server-side from the auth context — a wrong-tenant or unknown id returns a
 * clean 404 (SEC-11); the SPA NEVER sends a tenant id param (threat T-22-tenant). The
 * request goes through `apiFetch` (`credentials:'include'` + `X-Tenant-Subdomain`).
 */
export function employeeQueryKey(employeeId: number): readonly unknown[] {
  return ['employee', employeeId] as const;
}

export function useEmployeeQuery(employeeId: number) {
  return useQuery<EmployeeDetailResponse>({
    queryKey: employeeQueryKey(employeeId),
    queryFn: () => apiFetchJson<EmployeeDetailResponse>(`/api/v1/employees/${employeeId}`),
    enabled: Number.isFinite(employeeId) && employeeId > 0,
  });
}

/**
 * Employee write mutations (Plan 22-24): create / edit / archive / restore. Every write rides
 * `apiFetch` (credentials + CSRF double-submit + tenant header baked in) and sends NO tenantId —
 * the server resolves the tenant from the auth principal (SEC-11). On success each invalidates the
 * employee list + detail caches so the directory re-syncs to the authoritative server state.
 */
function invalidateEmployees(queryClient: ReturnType<typeof useQueryClient>, employeeId?: number) {
  void queryClient.invalidateQueries({ queryKey: ['employees'] });
  if (employeeId) void queryClient.invalidateQueries({ queryKey: employeeQueryKey(employeeId) });
}

/** Create a new employee (POST /api/v1/employees). */
export function useCreateEmployeeMutation() {
  const queryClient = useQueryClient();
  return useMutation<EmployeeWriteResponse, Error, EmployeeWriteInput>({
    mutationFn: (input) =>
      apiFetchJson<EmployeeWriteResponse>('/api/v1/employees', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateEmployees(queryClient),
  });
}

/** Edit an existing employee (PATCH /api/v1/employees/:id). */
export function useUpdateEmployeeMutation(employeeId: number) {
  const queryClient = useQueryClient();
  return useMutation<EmployeeWriteResponse, Error, EmployeeWriteInput>({
    mutationFn: (input) =>
      apiFetchJson<EmployeeWriteResponse>(`/api/v1/employees/${employeeId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateEmployees(queryClient, employeeId),
  });
}

/** Archive or restore an employee (POST /api/v1/employees/:id/archive|restore). */
export function useArchiveEmployeeMutation() {
  const queryClient = useQueryClient();
  return useMutation<EmployeeWriteResponse, Error, { employeeId: number; restore: boolean }>({
    mutationFn: ({ employeeId, restore }) =>
      apiFetchJson<EmployeeWriteResponse>(
        `/api/v1/employees/${employeeId}/${restore ? 'restore' : 'archive'}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
      ),
    onSuccess: (_data, { employeeId }) => invalidateEmployees(queryClient, employeeId),
  });
}
